<template>
  <VModal :showing="showing" container-classes="w-192" @update:showing="$emit('close')">
    <template #header>
      <h2 class="mb-0 text-lg font-medium text-gray-900">Choose from a previously uploaded file</h2>
      <p class="mb-0 text-sm text-gray-700 capitalize">{{ action }} {{ fileType }} Slide</p>
    </template>

    <template #default>
      <div v-if="noShelfFiles">
        <div class="mx-auto mt-12 font-light text-center text-gray-600 mb-14">
          <p class="mb-4 text-xl">No files uploaded yet!</p>
          <p class="mb-0">Upload your first files to get started</p>
        </div>
      </div>
      <component
        :is="type === 'table' ? VSearchableTable : VSearchableThumbnailList"
        v-else
        ref="tableRef"
        v-model:search="search"
        :auto-load="true"
        :options="pdfList"
        :current-item="currentFile"
        :loading="loading"
        :count="count"
        @selected="onSelect"
        @load-more="scroll"
      >
        <template #headers>
          <th class="pl-6">&nbsp;</th>
          <th @click="sortOn('name')">
            File Name
            <FontAwesomeIcon v-if="sortKey === 'name'" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" class="ml-2" />
          </th>
          <th class="pr-6 text-right" @click="sortOn('updated_at')">
            Uploaded
            <FontAwesomeIcon v-if="sortKey === 'updated_at'" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" class="ml-2" />
          </th>
        </template>

        <template #option="{ item }">
          <td class="w-20 pl-6"><img class="w-auto h-12 rounded-md" :src="item.thumbnail" /></td>
          <td>{{ item.name }}</td>
          <td class="w-48 pr-6 text-sm font-normal text-right whitespace-nowrap">{{ formatDateTimeMed(item.updated_at) }}</td>
        </template>
      </component>
    </template>

    <template #footer>
      <slot name="footer">
        <div class="flex justify-end gap-2">
          <VButton variant="light" @click="$emit('back')">Back</VButton>
          <VButton v-if="noShelfFiles" variant="copper" @click="openFileUploadDialog">
            <FontAwesomeIcon v-if="uploading" class="mr-2 text-gray-600" icon="circle-notch" spin />
            Upload file
            <input ref="fileField" class="hidden" type="file" :accept="mimeType" @change="handleFiles" />
          </VButton>
        </div>
      </slot>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { ShelfFile } from '@/entities/shelfFile'
import type { QueryParameters } from '@/composables/useIndexSearch'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

interface ShelfQuery extends QueryParameters {
  sortKey: 'name' | 'updated_at'
  sortDir: '-' | ''
  folderId: number
}
interface ApiQuery {
  sort_by: `${ShelfQuery['sortDir']}${ShelfQuery['sortKey']}`
  folder_id: number
  query: string | null
  limit: number
}

const props = defineProps({
  companyId: {
    type: Number as PropType<number | null>,
    required: true,
  },
  folderId: {
    type: Number,
    default: null,
  },
  showing: {
    type: Boolean,
    default: false,
  },
  currentFile: {
    type: Object as PropType<ShelfFile | null>,
    default: null,
  },
  fileType: {
    type: String,
    required: true,
  },
  action: {
    type: String,
    default: 'create',
  },
  type: {
    type: String as PropType<'table' | 'images' | 'videos'>,
    default: 'table',
  },
  mimeType: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['choose-file', 'close', 'back', 'error'])

const VSearchableTable = resolveComponent('VSearchableTable')
const VSearchableThumbnailList = resolveComponent('VSearchableThumbnailList')

const tableRef = ref<any | undefined>()
const pdfList = computed(() => files.value ?? [])
const noShelfFiles = computed(() => !files.value.length && !loading.value && !search.value)
const fileField = ref<HTMLInputElement | null>(null)

const {
  results: files,
  query: search,
  count,
  continuation,
  loading,
  sortKey,
  sortDir,
  folderId,
  refresh,
} = useIndexSearch<ShelfFile, ShelfQuery, ApiQuery>({
  initParams: { limit: 12, sortDir: '-', sortKey: 'updated_at', folderId: props.folderId },
  endpoint: () => `v2/companies/${props.companyId}/shelf/files`,
  finalParameters: ({ query, limit, sortKey, sortDir, folderId }) => ({
    sort_by: `${sortDir}${sortKey}`,
    folder_id: folderId,
    query,
    limit,
  }),
})

watch(
  () => props.currentFile,
  () => {
    // Make sure selected value is within viewport
    tableRef.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
  },
  { flush: 'post' }
)

watch(
  () => props.folderId,
  id => {
    if (!id) return

    folderId.value = id
  }
)

const sortOn = (key: ShelfQuery['sortKey']) => {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}
//
async function scroll() {
  if (!loading.value && count.value > files.value.length) {
    continuation.value++
  }
}

function onSelect(selectedPdf: ShelfFile): void {
  emit('choose-file', selectedPdf)
}

watch(
  () => props.showing,
  showing => {
    if (showing) refresh()
  }
)

const { companyId } = toRefs(props)
const { loading: uploading, handleFiles } = useShelfUpload({
  companyId,
  folderId,
  onError(err: string) {
    emit('error', err)
  },
  clearError() {
    emit('error', null)
  },
  onUpload(payload: any) {
    emit('choose-file', payload)
  },
})

function openFileUploadDialog() {
  fileField.value?.click()
}
</script>

<style lang="stylus" scoped>
.v-searchable-table
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply text-gray-800 p-4 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
</style>
